<template>
  <div
    class="mx-3"
  >
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="font-weight-bold h5 mb-0">
        {{ $t('shop.deliveryPlan.ingredients') }}
      </div>
      <button
        class="btn btn-sm btn-primary"
        @click="openCreateModal"
      >
        <i class="fas fa-plus mr-1" />
        {{ $t('general.add') }}
      </button>
    </div>

    <!-- Search Bar -->
    <div class="mb-3">
      <div class="input-group">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          :placeholder="`${$t('general.search')}...`"
        >
      </div>
    </div>

    <div v-if="pending">
      <Loader />
    </div>

    <div
      v-else
      style="height: calc(100vh - 300px); overflow: auto; padding-bottom: 100px"
    >
      <!-- Ingredients List -->
      <div
        v-if="filteredIngredients.length === 0"
        class="text-muted text-center py-4"
      >
        <i class="far fa-carrot fa-2x mb-2" />
        <div v-if="searchQuery">
          {{ $t('shop.deliveryPlan.noIngredientsFound', { searchQuery }) }}
        </div>
        <div v-else>
          {{ $t('shop.deliveryPlan.noIngredientsAvailable') }}
        </div>
      </div>

      <div
        v-else
        class="list-group"
      >
        <div
          v-for="ingredient in filteredIngredients"
          :key="ingredient.id"
          class="list-group-item d-flex justify-content-between align-items-center"
        >
          <div>
            <div class="font-weight-bold">
              {{ ingredient.name }}
              <span class="text-muted small ml-2 pb-2">
                <span v-if="ingredient.weightUnit">
                  {{ ingredient.weightUnit }}
                </span>
                <span v-if="ingredient.weightUnit && ingredient.countUnit"> | </span>
                <span v-if="ingredient.countUnit">
                  {{ ingredient.countUnit }}
                </span>
              </span>
            </div>
          </div>
          <div class="d-flex">
            <button
              class="btn btn-sm btn-outline-primary mr-2"
              title="Edit ingredient"
              @click="openEditModal(ingredient)"
            >
              <i class="fas fa-edit" />
            </button>
            <Tippy
              trigger="click"
              theme="light"
              arrow
              interactive
            >
              <template #trigger>
                <button
                  class="btn btn-sm btn-outline-danger"
                  title="Delete ingredient"
                >
                  <i class="fas fa-trash" />
                </button>
              </template>

              <div
                class="mb-2 font-weight-bold"
                style="max-width: 250px"
              >
                Uwaga! Składnik zostanie usunięty ze wszystkich posiłków!
              </div>
              <button
                class="btn btn-sm btn-block btn-outline-danger"
                title="Delete ingredient"
                @click="deleteIngredient(ingredient)"
              >
                <i class="fas fa-trash" />
                {{ $t('general.remove') }}
              </button>
            </Tippy>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div
      class="modal fade"
      :class="{ show: showModal }"
      :style="{ display: showModal ? 'block' : 'none' }"
      tabindex="-1"
    >
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title font-weight-bold">
              <i class="far fa-carrot mr-2" />
              {{ editingIngredient ? $t('shop.deliveryPlan.editIngredient') : $t('shop.deliveryPlan.addIngredient') }}
            </h5>
            <button
              type="button"
              class="close"
              @click="closeModal"
            >
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveIngredient">
              <div class="form-group">
                <label for="ingredientName">
                  {{ $t('shop.deliveryPlan.ingredientName') }}
                </label>
                <input
                  id="ingredientName"
                  v-model="form.name"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': errors.name }"
                  :placeholder="$t('shop.deliveryPlan.ingredientName')"
                  required
                >
                <div
                  v-if="errors.name"
                  class="invalid-feedback"
                >
                  {{ errors.name }}
                </div>
              </div>

              <div class="form-group">
                <label for="weightUnit">
                  {{ $t('shop.deliveryPlan.weightUnit') }}
                </label>
                <input
                  id="weightUnit"
                  v-model="form.weightUnit"
                  type="text"
                  class="form-control"
                  placeholder="np., kg, g, oz"
                >
              </div>

              <div class="form-group">
                <label for="countUnit">
                  {{ $t('shop.deliveryPlan.countUnit') }}
                </label>
                <input
                  id="countUnit"
                  v-model="form.countUnit"
                  type="text"
                  class="form-control"
                  placeholder="np., szt, kubki, łyżki"
                >
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="saving"
              @click="saveIngredient"
            >
              <span v-if="saving">
                <i class="fas fa-spinner fa-spin mr-1" />
                Saving...
              </span>
              <span v-else>
                {{ editingIngredient ? $t('general.save') : $t('general.add') }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Backdrop -->
    <div
      v-if="showModal"
      class="modal-backdrop fade show"
      @click="closeModal"
    />
  </div>
</template>

<script>
import axios from '@/api/axios';

export default {
  props: {
    catalogId: String,
  },
  data: () => ({
    ingredients: [],
    pending: false,
    saving: false,
    searchQuery: '',
    showModal: false,
    editingIngredient: null,
    form: {
      name: '',
      weightUnit: '',
      countUnit: '',
    },
    errors: {},
  }),
  computed: {
    filteredIngredients() {
      if (!this.searchQuery.trim()) {
        return this.ingredients;
      }
      const query = this.searchQuery.toLowerCase();
      return this.ingredients.filter((ingredient) =>
        ingredient.name.toLowerCase().includes(query));
    },
  },
  methods: {
    loadIngredients() {
      if (this.ingredients.length === 0) {
        this.pending = true;
      }
      axios.get(`shop/${this.catalogId}/meals/ingredients`)
        .then(({ data }) => {
          this.ingredients = data.ingredients;
        })
        .catch(() => {
          this.$toast.error('Failed to load ingredients');
        })
        .finally(() => {
          this.pending = false;
        });
    },

    openCreateModal() {
      this.editingIngredient = null;
      this.resetForm();
      this.showModal = true;
    },

    openEditModal(ingredient) {
      this.editingIngredient = ingredient;
      this.form = {
        name: ingredient.name || '',
        weightUnit: ingredient.weightUnit || '',
        countUnit: ingredient.countUnit || '',
      };
      this.errors = {};
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.editingIngredient = null;
      this.resetForm();
    },

    resetForm() {
      this.form = {
        name: '',
        weightUnit: '',
        countUnit: '',
      };
      this.errors = {};
    },

    validateForm() {
      this.errors = {};

      if (!this.form.name.trim()) {
        this.errors.name = 'Name is required';
      }

      return Object.keys(this.errors).length === 0;
    },

    async saveIngredient() {
      if (!this.validateForm()) {
        return;
      }

      this.saving = true;

      try {
        if (this.editingIngredient) {
          await axios.put(`shop/${this.catalogId}/meals/ingredient/${this.editingIngredient.id}`, this.form);
        } else {
          await axios.post(`shop/${this.catalogId}/meals/ingredient`, this.form);
        }

        this.closeModal();
        this.loadIngredients();
      } catch (error) {
        this.$toast.error('Failed to save ingredient');
      } finally {
        this.saving = false;
      }
    },

    async deleteIngredient(ingredient) {
      try {
        await axios.delete(`shop/${this.catalogId}/meals/ingredient/${ingredient.id}`);
        this.loadIngredients();
      } catch (error) {
        this.$toast.error('Failed to delete ingredient');
      }
    },
  },
  created() {
    this.loadIngredients();
  },
};
</script>

<style scoped>
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.btn-group .btn {
  margin-left: 0.25rem;
}

.btn-group .btn:first-child {
  margin-left: 0;
}
</style>
